import { supabase } from '@/shared/lib/supabase'
import type { ElectronicSignatureRecord, ElectronicSignatureMode, PdfPlacement } from '../types'

// Columns readable by anon after the 2026-05-24 column-lockdown migration.
// PII (signer_email, signer_user_id, ip_address, user_agent, biometric_data)
// is REVOKEd from anon; selecting them returns 42501.
const PUBLIC_SIGNATURE_COLUMNS =
  'id,signature_id,signer_name,signature_type,signature_content_text,signature_image_data,original_filename,signed_filename,original_hash,signed_hash,hash_algorithm,page_number,position_x,position_y,position_width,position_height,page_width,page_height,signed_at,legal_framework,legal_implication,short_id'

interface CreateRecordInput {
  signatureId: string
  shortId: string
  signerEmail: string
  signerName?: string
  mode: ElectronicSignatureMode
  signatureText?: string
  originalFilename: string
  signedFilename: string
  originalHash: string
  signedHash: string
  placement: PdfPlacement
  pageWidth: number
  pageHeight: number
}

export async function createElectronicSignatureRecord(
  input: CreateRecordInput
): Promise<ElectronicSignatureRecord> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data: userData } = await supabase.auth.getUser()
  if (!userData.user) throw new Error('Sesion expirada. Inicia sesion nuevamente.')

  const row = {
    signature_id: input.signatureId,
    short_id: input.shortId,
    signer_user_id: userData.user.id,
    signer_email: input.signerEmail,
    signer_name: input.signerName ?? null,
    signature_type: input.mode,
    signature_content_text: input.signatureText ?? null,
    original_filename: input.originalFilename,
    signed_filename: input.signedFilename,
    original_hash: input.originalHash,
    signed_hash: input.signedHash,
    hash_algorithm: 'SHA-256',
    page_number: input.placement.pageNumber,
    position_x: input.placement.x,
    position_y: input.placement.y,
    position_width: input.placement.width,
    position_height: input.placement.height,
    page_width: input.pageWidth,
    page_height: input.pageHeight,
    user_agent: navigator.userAgent,
  }

  const { data, error } = await supabase
    .from('sgw_electronic_signatures')
    .insert(row)
    .select(PUBLIC_SIGNATURE_COLUMNS)
    .single()

  if (error) throw new Error(`Error guardando firma: ${error.message}`)
  return data as ElectronicSignatureRecord
}

export async function getElectronicSignatureRecord(
  signatureId: string
): Promise<ElectronicSignatureRecord | null> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase
    .from('sgw_electronic_signatures')
    .select(PUBLIC_SIGNATURE_COLUMNS)
    .eq('signature_id', signatureId)
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data as ElectronicSignatureRecord | null
}

/**
 * Lookup unificado por short_id en ambas tablas.
 * Devuelve el tipo y el record para que el caller decida que UI mostrar.
 */
export async function getSignatureByShortId(shortId: string): Promise<
  | { kind: 'electronic'; record: ElectronicSignatureRecord }
  | { kind: 'digital'; record: Record<string, unknown> }
  | null
> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data: digital, error: dErr } = await supabase
    .from('sgw_signatures')
    .select('*')
    .eq('short_id', shortId)
    .maybeSingle()
  if (dErr) throw new Error(dErr.message)
  if (digital) return { kind: 'digital', record: digital as Record<string, unknown> }

  const { data: electronic, error: eErr } = await supabase
    .from('sgw_electronic_signatures')
    .select(PUBLIC_SIGNATURE_COLUMNS)
    .eq('short_id', shortId)
    .maybeSingle()
  if (eErr) throw new Error(eErr.message)
  if (electronic) return { kind: 'electronic', record: electronic as ElectronicSignatureRecord }

  return null
}
